/**
 * Contact Page – super clean, minimal
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
} from '@mui/material';
import {
  EmailOutlined as EmailIcon,
  LanguageOutlined as WebIcon,
  LocationOnOutlined as LocationIcon,
} from '@mui/icons-material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function Contact() {
  const { i18n } = useCustomTranslation();
  const isEn = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/contact', 'Contact');
    document.title = isEn ? 'Contact Us | PayWise365' : '聯絡我們 | PayWise365';
  }, [isEn]);

  const seoTitle = isEn
    ? 'Contact PayWise365 - Hong Kong Mortgage Calculator Support'
    : '聯絡 PayWise365 - 香港按揭計算機支援';

  const seoDescription = isEn
    ? 'Get in touch with PayWise365 for questions about our mortgage calculators, financial planning tools, or partnership inquiries. Based in Hong Kong, serving homeowners across Asia.'
    : '聯絡 PayWise365，查詢有關我們的按揭計算機、理財規劃工具或合作查詢。總部位於香港，服務亞洲各地業主。';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalUrl="https://www.paywise365.com/contact"
        ogType="website"
      />
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
            {isEn ? 'Contact Us' : '聯絡我們'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {isEn ? "We'd love to hear from you" : '歡迎與我們聯繫'}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="sm" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack spacing={3}>
          {/* Email */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <EmailIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                {isEn ? 'Email' : '電郵'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="a"
                href="mailto:paywise365@gmail.com"
                sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
              >
                paywise365@gmail.com
              </Typography>
            </Box>
          </Paper>

          {/* Website */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <WebIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                {isEn ? 'Website' : '網站'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="a"
                href="https://www.paywise365.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
              >
                www.paywise365.com
              </Typography>
            </Box>
          </Paper>

          {/* Location */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <LocationIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                {isEn ? 'Location' : '地區'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hong Kong
              </Typography>
            </Box>
          </Paper>
        </Stack>

        {/* Note */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 5, textAlign: 'center', lineHeight: 1.7 }}
        >
          {isEn
            ? 'We typically respond within 1–2 business days. For general enquiries or feedback, please email us directly.'
            : '我們一般會於1-2個工作天內回覆。如有一般查詢或意見，請直接電郵我們。'}
        </Typography>
      </Container>
    </Box>
  );
}
